import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", cif: "", phone: "", email: "",
  address: "", postal_code: "", city: "", province: "", country: "España",
};

export default function ClientesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const r = await api.get("/clients"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editing) await api.put(`/clients/${editing.id}`, { ...editing, ...form });
      else await api.post("/clients", form);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar cliente?")) return;
    try { await api.delete(`/clients/${id}`); load(); toast.success("Cliente borrado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };
  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const norm = (s) => (s || "").toString().toLowerCase();
  const filtered = items.filter((c) => {
    if (!q.trim()) return true;
    const n = norm(q);
    return [c.name, c.cif, c.phone, c.email, c.city, c.province].some((f) => norm(f).includes(n));
  });

  return (
    <div data-testid="clientes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="section-title">Clientes</h1>
          <p className="text-sm text-zinc-500 mt-1">Reutilizables en presupuestos y facturas.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-client" onClick={startNew}><Plus size={16} className="mr-1" />Nuevo cliente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["name", "Nombre / Razón social *"], ["cif", "CIF/NIF"],
                ["phone", "Teléfono"], ["email", "Email"],
                ["address", "Dirección"], ["postal_code", "Código postal"],
                ["city", "Ciudad"], ["province", "Provincia"],
                ["country", "País"],
              ].map(([k, label]) => (
                <div key={k} className={k === "address" ? "col-span-2" : ""}>
                  <Label className="text-xs">{label}</Label>
                  <Input data-testid={`client-${k}`} value={form[k] || ""}
                         onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-client" onClick={submit}>{editing ? "Guardar" : "Crear"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="clients-search"
            placeholder="Buscar por nombre, CIF, email, ciudad…"
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="text-xs text-zinc-400">{filtered.length} / {items.length}</span>
      </div>

      <div className="page-card overflow-x-auto">
        <table className="tbl" data-testid="clients-table">
          <thead><tr><th>Nombre</th><th>CIF</th><th>Teléfono</th><th>Email</th><th>Ciudad</th><th></th></tr></thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`}>
                <td className="font-medium">{c.name}</td>
                <td>{c.cif || "—"}</td>
                <td>{c.phone || "—"}</td>
                <td>{c.email || "—"}</td>
                <td>{c.city || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => startEdit(c)} className="px-2 py-1 hover:bg-zinc-100 rounded" data-testid={`edit-client-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="px-2 py-1 hover:bg-red-50 text-red-600 rounded" data-testid={`del-client-${c.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="text-center text-zinc-400 py-10">{items.length === 0 ? "Sin clientes todavía." : "Sin resultados."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
